import {
  Autocomplete,
  Checkbox,
  FormControl,
  TextField,
  TextFieldVariants,
} from "@mui/material";
import {
  RegisterOptions,
  FieldValues,
  Controller,
  ControllerRenderProps,
  FieldError,
} from "react-hook-form";
import { MenuData } from "./CustomSelect";
import LabelInput from "./LabelInput";
import { useEffect, useState } from "react";
import { CustomFormHelperText } from "./CustomFormHelperText";
import { useTranslation } from "react-i18next";
import { common } from "../../i18n";

// Sentinel value for the synthetic "select all" option — never collides with
// a real refId/name since listMenuItem values come from business data.
const ALL_OPTION_VALUE = "__ALL__";

interface Props {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  rules?:
    | Omit<
        RegisterOptions<FieldValues, string>,
        "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
      >
    | undefined;
  label: string;
  info: string;
  placeholder: string;
  listMenuItem: MenuData[];
  required?: boolean;
  disabled?: boolean;
  defaultValue?: any;
  showLabelInput?: boolean | undefined;
  variant?: TextFieldVariants;
  /** When true, RHF stores item.value (e.g. refId) instead of nameEN.
   *  Display chips still show nameEN. Default false (backward compat). */
  storeValue?: boolean;
}

// ----------------------------------------------------------------------
// 1. แยก Component ย่อยออกมา เพื่อให้ใช้ Hooks ได้ถูกต้อง
// ----------------------------------------------------------------------
interface InnerProps {
  field: ControllerRenderProps<any, string>;
  error?: FieldError;
  mainProps: Props;
  options: MenuData[];
}

const MultipleAutoCompleteRender = ({
  field: { value, onChange: rfhOnChange, onBlur: rfhOnBlur },
  error,
  mainProps: props,
  options,
}: InnerProps) => {
  const { t } = useTranslation([common], { nsMode: "fallback" });

  // Key used to match a stored RHF value back to its MenuData entry —
  // refId when storeValue is true (always unique), nameEN otherwise
  // (legacy/backward-compat path, same collision risk as before).
  const keyOf = (item: MenuData): string =>
    props.storeValue ? item.value : item.nameEN;

  const toMenuItems = (vals: string[]): MenuData[] =>
    vals
      .map((v) => props.listMenuItem.find((item) => keyOf(item) === v))
      .filter((item): item is MenuData => item !== undefined);

  const [pendingValue, setPendingValue] = useState<MenuData[]>(() =>
    toMenuItems(value || []),
  );

  // Sync Local State — also self-heals: if a stored value no longer
  // resolves against listMenuItem (e.g. the sub-org was deleted), drop it
  // from both the display and the RHF value instead of leaving it stuck.
  // Guarded on listMenuItem.length > 0 so we never wipe a valid selection
  // just because listMenuItem hasn't finished loading yet.
  useEffect(() => {
    const vals: string[] = value || [];
    const resolved = toMenuItems(vals);
    setPendingValue(resolved);

    if (props.listMenuItem.length > 0 && resolved.length !== vals.length) {
      rfhOnChange(resolved.map((item) => keyOf(item)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, props.listMenuItem, props.storeValue]);

  return (
    <>
      {(props.showLabelInput === undefined ||
        props.showLabelInput === true) && (
        <LabelInput
          label={props.label}
          info={props.info}
          required={props.required}
        />
      )}

      <Autocomplete
        size="small"
        multiple
        limitTags={2}
        options={options}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        getOptionKey={(option) => option.value}
        getOptionLabel={(option) => option.nameEN}
        disableCloseOnSelect
        disabled={props.disabled}
        value={pendingValue}
        onChange={(event, newValue: MenuData[], reason) => {
          let valueTemp = [...newValue];

          if (
            reason === "selectOption" &&
            valueTemp.some((v) => v.value === ALL_OPTION_VALUE)
          ) {
            const isAllSelected =
              pendingValue.length === props.listMenuItem.length;

            if (isAllSelected) {
              valueTemp = [];
            } else {
              valueTemp = props.listMenuItem.filter((item) => !item.disabled);
            }
          } else if (reason === "removeOption") {
            valueTemp = valueTemp.filter((v) => v.value !== ALL_OPTION_VALUE);
          } else if (reason === "clear") {
            // ดักจับเวลากดปุ่ม ❌ ล้างค่าทั้งหมด
            valueTemp = [];
          }

          setPendingValue(valueTemp);

          // ✅ อัปเดต React Hook Form ทันทีที่ข้อมูลมีการเปลี่ยนแปลง (เลือก, ลบ Tag, เคลียร์)
          rfhOnChange(valueTemp.map((item) => keyOf(item)));
        }}
        onClose={() => {
          // ✅ ให้ trigger แค่ onBlur ตอนปิด Dropdown เพื่อเช็ค Validation (เช่น required)
          rfhOnBlur();
        }}
        getOptionDisabled={(option) => option.disabled ?? false}
        renderOption={({ key, ...optionProps }, option, { selected }) => {
          const isAllSelected =
            pendingValue.length === props.listMenuItem.length &&
            props.listMenuItem.length > 0;
          const isChecked =
            option.value === ALL_OPTION_VALUE ? isAllSelected : selected;

          return (
            <li key={key} {...optionProps}>
              <Checkbox style={{ marginRight: 8 }} checked={isChecked} />
              {option.nameEN}
            </li>
          );
        }}
        sx={{
          width: "100%",
          backgroundColor: "var(--white)",
          margin:
            props.showLabelInput === undefined || props.showLabelInput === true
              ? undefined
              : 0,
          "& .MuiAutocomplete-tag": {
            maxWidth: "40%",
            fontSize: "12px",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={props.variant}
            label={
              props.showLabelInput === undefined ||
              props.showLabelInput === true ||
              props.variant === "standard"
                ? undefined
                : props.label
            }
            sx={{
              zIndex: 0,
              "& .MuiOutlinedInput-root": {
                paddingTop: "8px",
                paddingBottom: "8px",
              },
              "& .MuiInputBase-sizeSmall": {
                paddingTop: "5px",
                paddingBottom: "5px",
              },
            }}
            error={!!error}
          />
        )}
      />
      <CustomFormHelperText error={error} />
    </>
  );
};

// ----------------------------------------------------------------------
// 2. Component หลัก เหลือหน้าที่แค่เตรียม Options และเรียก Controller
// ----------------------------------------------------------------------
export const MultipleAutoComplete = ({
  variant = "outlined",
  ...props
}: Props) => {
  const { t } = useTranslation([common], {
    nsMode: "fallback",
  });

  const [options, setOptions] = useState<MenuData[]>([]);

  useEffect(() => {
    const allOption: MenuData = {
      value: ALL_OPTION_VALUE,
      nameEN: t("All"),
      nameTH: t("All"),
    };
    setOptions([allOption, ...props.listMenuItem]);
  }, [props.listMenuItem, t]);

  return (
    <FormControl fullWidth>
      <Controller
        name={props.name}
        control={props.control}
        rules={props.rules}
        defaultValue={props.defaultValue ?? []}
        render={({ field, fieldState: { error } }) => {
          // ส่งต่อ props ไปยัง Component ย่อย
          return (
            <MultipleAutoCompleteRender
              field={field}
              error={error}
              mainProps={{ ...props, variant }}
              options={options}
            />
          );
        }}
      />
    </FormControl>
  );
};
